"use client";

import type React from "react";

import { ArrowLeft, ArrowRight, Award, CheckCircle, ChevronRight, Clock, CreditCard, MessageSquare, Phone, ShieldCheck, ShoppingBag, Smartphone, Star, ThumbsUp, Users, X, Zap } from "lucide-react";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import Image from "next/image";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

// Service data
const services = [
  {
    id: "mobile",
    title: "Mobilfunk & Spielekarten",
    description: "Wir bieten Prepaid-Guthaben für Mobiltelefone und Zahlungskarten für Gaming-Plattformen an.",
    longDescription:
      "Unser Mobilfunk- und Spielekartenservice bietet Ihnen eine breite Palette an Prepaid-Guthaben für alle deutschen Mobilfunkanbieter sowie Zahlungskarten für die beliebtesten Gaming-Plattformen. Mit unserer sofortigen digitalen Lieferung können Sie Ihr Guthaben innerhalb von Minuten aufladen oder Ihre Lieblingsspiele ohne Verzögerung genießen. Wir bieten wettbewerbsfähige Preise und regelmäßige Sonderangebote, um sicherzustellen, dass Sie immer den besten Wert für Ihr Geld erhalten.",
    icon: Smartphone,
    features: [
      "Prepaid-Guthaben für alle deutschen Netze",
      "Gaming-Plattform Zahlungskarten",
      "Sofortige digitale Lieferung",
      "Wettbewerbsfähige Preise",
      "Sichere Zahlungsabwicklung",
      "24/7 Kundensupport",
    ],
    benefits: [
      {
        title: "Sofortige Lieferung",
        description: "Erhalten Sie Ihr Guthaben innerhalb von Sekunden nach dem Kauf",
        icon: Zap,
      },
      {
        title: "Breite Auswahl",
        description: "Unterstützung für alle großen Anbieter und Gaming-Plattformen",
        icon: Star,
      },
      {
        title: "Zuverlässiger Service",
        description: "Tausende zufriedener Kunden vertrauen auf unseren Service",
        icon: ThumbsUp,
      },
    ],
    testimonials: [
      {
        name: "Thomas Müller",
        position: "Regelmäßiger Kunde",
        content: "Ich nutze den Prepaid-Service seit über einem Jahr und bin sehr zufrieden mit der schnellen Lieferung und dem ausgezeichneten Kundenservice.",
        rating: 5,
      },
      {
        name: "Laura Schmidt",
        position: "Gaming-Enthusiast",
        content: "Die Gaming-Karten sind immer sofort verfügbar und zu einem guten Preis. Definitiv meine erste Wahl!",
        rating: 5,
      },
    ],
    stats: [
      { value: "99.9%", label: "Verfügbarkeit" },
      { value: "< 5 Sek", label: "Lieferzeit" },
      { value: "24/7", label: "Support" },
    ],
    imageSrc: "/placeholder.svg?height=400&width=600",
    imageAlt: "Mobile phone credit and game cards",
    color: "bg-red-50 dark:bg-red-950/30",
    accentColor: "red",
    gradientFrom: "from-red-600",
    gradientTo: "to-orange-400",
  },
  {
    id: "ecommerce",
    title: "E-Commerce für Mobilgeräte",
    description: "Unser E-Commerce-Bereich bietet eine breite Palette von Mobiltelefonen und Zubehör.",
    longDescription:
      "Unser E-Commerce-Bereich für Mobilgeräte bietet Ihnen Zugang zu den neuesten Smartphones, Tablets und Zubehör zu wettbewerbsfähigen Preisen. Wir führen nur Originalprodukte und autorisiertes Zubehör, um sicherzustellen, dass Sie qualitativ hochwertige Produkte erhalten. Mit unserem schnellen Versand in ganz Deutschland erhalten Sie Ihre Bestellung innerhalb weniger Tage. Unsere kundenfreundlichen Rückgabebedingungen geben Ihnen zusätzliche Sicherheit bei Ihrem Einkauf.",
    icon: ShoppingBag,
    features: [
      "Große Auswahl an Smartphones",
      "Original-Zubehör und Ersatzteile",
      "Schneller Versand in ganz Deutschland",
      "Kundenfreundliche Rückgabebedingungen",
      "Sichere Zahlungsmethoden",
      "Regelmäßige Sonderangebote",
    ],
    benefits: [
      {
        title: "Qualitätsgarantie",
        description: "Nur Originalprodukte und autorisiertes Zubehör",
        icon: Award,
      },
      {
        title: "Schneller Versand",
        description: "Lieferung innerhalb von 1-3 Werktagen in ganz Deutschland",
        icon: Clock,
      },
      {
        title: "Einfache Rückgabe",
        description: "30 Tage Rückgaberecht ohne komplizierte Bedingungen",
        icon: ThumbsUp,
      },
    ],
    testimonials: [
      {
        name: "Markus Weber",
        position: "Technik-Enthusiast",
        content: "Die Auswahl an Smartphones ist beeindruckend und die Preise sind sehr wettbewerbsfähig. Der Versand war schneller als erwartet.",
        rating: 5,
      },
      {
        name: "Julia Becker",
        position: "Geschäftskundin",
        content: "Wir haben mehrere Geräte für unser Büro bestellt und waren mit der Qualität und dem Service sehr zufrieden.",
        rating: 4,
      },
    ],
    stats: [
      { value: "1000+", label: "Produkte" },
      { value: "1-3 Tage", label: "Lieferzeit" },
      { value: "30 Tage", label: "Rückgaberecht" },
    ],
    imageSrc: "/placeholder.svg?height=400&width=600",
    imageAlt: "E-commerce mobile phone products",
    color: "bg-orange-50 dark:bg-orange-950/30",
    accentColor: "orange",
    gradientFrom: "from-orange-500",
    gradientTo: "to-yellow-400",
  },
  {
    id: "b2b",
    title: "B2B Software-Lösungen",
    description: "Maßgeschneiderte Software-Lösungen für Unternehmen jeder Größe.",
    longDescription:
      "Unsere B2B-Softwarelösungen sind darauf ausgerichtet, die Effizienz und Produktivität Ihres Unternehmens zu steigern. Wir bieten maßgeschneiderte Softwareentwicklung, die genau auf Ihre Geschäftsanforderungen zugeschnitten ist. Unsere integrierten Geschäftslösungen helfen Ihnen, verschiedene Aspekte Ihres Unternehmens nahtlos zu verwalten. Mit unseren cloud-basierten Diensten können Sie von überall aus auf Ihre Daten und Anwendungen zugreifen. Unser technischer Support steht Ihnen rund um die Uhr zur Verfügung, um sicherzustellen, dass Ihre Systeme reibungslos funktionieren.",
    icon: ShieldCheck,
    features: [
      "Kundenspezifische Softwareentwicklung",
      "Integrierte Geschäftslösungen",
      "Cloud-basierte Dienste",
      "Technischer Support rund um die Uhr",
      "Skalierbare Lösungen",
      "Datensicherheit und Compliance",
    ],
    benefits: [
      {
        title: "Maßgeschneiderte Lösungen",
        description: "Auf Ihre spezifischen Geschäftsanforderungen zugeschnitten",
        icon: ShieldCheck,
      },
      {
        title: "Expertenteam",
        description: "Erfahrene Entwickler und Berater für Ihr Projekt",
        icon: Users,
      },
      {
        title: "Kontinuierliche Unterstützung",
        description: "Technischer Support und Wartung rund um die Uhr",
        icon: Clock,
      },
    ],
    testimonials: [
      {
        name: "Dr. Andreas Schneider",
        position: "CTO, TechSolutions GmbH",
        content: "Die maßgeschneiderte Software hat unsere Geschäftsprozesse revolutioniert und unsere Effizienz um 40% gesteigert.",
        rating: 5,
      },
      {
        name: "Sabine Hoffmann",
        position: "Geschäftsführerin, Hoffmann & Partner",
        content: "Die Zusammenarbeit war von Anfang an professionell und die gelieferte Lösung übertraf unsere Erwartungen.",
        rating: 5,
      },
    ],
    stats: [
      { value: "100+", label: "Projekte" },
      { value: "98%", label: "Kundenzufriedenheit" },
      { value: "24/7", label: "Support" },
    ],
    imageSrc: "/placeholder.svg?height=400&width=600",
    imageAlt: "B2B software solutions",
    color: "bg-blue-50 dark:bg-blue-950/30",
    accentColor: "blue",
    gradientFrom: "from-blue-600",
    gradientTo: "to-indigo-400",
  },
  {
    id: "invoice",
    title: "E-Rechnungsdienste",
    description: "Vereinfachen Sie Ihre Rechnungsstellung mit unseren E-Rechnungsdiensten.",
    longDescription:
      "Unsere E-Rechnungsdienste helfen Ihnen, den Rechnungsstellungsprozess zu digitalisieren und zu automatisieren. Wir stellen sicher, dass Ihre elektronischen Rechnungen den gesetzlichen Anforderungen entsprechen und von den Steuerbehörden akzeptiert werden. Mit unserer automatisierten Rechnungsverarbeitung sparen Sie Zeit und reduzieren Fehler. Unsere Lösungen lassen sich nahtlos in gängige Buchhaltungssysteme integrieren, sodass Sie Ihre bestehenden Prozesse beibehalten können. Alle Ihre Rechnungsdaten werden sicher gespeichert und sind jederzeit zugänglich.",
    icon: MessageSquare,
    features: [
      "Gesetzeskonforme elektronische Rechnungen",
      "Automatisierte Rechnungsverarbeitung",
      "Integration mit gängigen Buchhaltungssystemen",
      "Sichere Datenspeicherung",
      "Einfache Archivierung und Suche",
      "Reduzierte Papierverwendung",
    ],
    benefits: [
      {
        title: "Zeit- und Kostenersparnis",
        description: "Reduzieren Sie den Verwaltungsaufwand und sparen Sie Kosten",
        icon: Clock,
      },
      {
        title: "Gesetzeskonform",
        description: "Alle Rechnungen entsprechen den aktuellen gesetzlichen Anforderungen",
        icon: ShieldCheck,
      },
      {
        title: "Nahtlose Integration",
        description: "Einfache Anbindung an Ihre bestehenden Systeme",
        icon: Zap,
      },
    ],
    testimonials: [
      {
        name: "Michael Wagner",
        position: "Finanzleiter, Wagner & Co.",
        content: "Die E-Rechnungsdienste haben unseren Rechnungsstellungsprozess erheblich beschleunigt und die Fehlerquote drastisch reduziert.",
        rating: 5,
      },
      {
        name: "Claudia Meyer",
        position: "Buchhalterin, Meyer GmbH",
        content: "Die Integration mit unserem Buchhaltungssystem war reibungslos und der Support bei Fragen ist hervorragend.",
        rating: 4,
      },
    ],
    stats: [
      { value: "60%", label: "Zeitersparnis" },
      { value: "100%", label: "Gesetzeskonform" },
      { value: "99.9%", label: "Verfügbarkeit" },
    ],
    imageSrc: "/placeholder.svg?height=400&width=600",
    imageAlt: "E-invoice services",
    color: "bg-green-50 dark:bg-green-950/30",
    accentColor: "green",
    gradientFrom: "from-green-600",
    gradientTo: "to-emerald-400",
  },
  {
    id: "payment",
    title: "Online-Zahlungslösungen",
    description: "Sichere und effiziente Zahlungslösungen für Ihr Unternehmen.",
    longDescription:
      "Unsere Online-Zahlungslösungen bieten Ihnen eine sichere und effiziente Möglichkeit, Zahlungen zu akzeptieren und zu verarbeiten. Wir unterstützen mehrere Zahlungsmethoden, darunter Kreditkarten, Lastschrift, PayPal und mehr, um sicherzustellen, dass Ihre Kunden mit ihrer bevorzugten Methode bezahlen können. Unsere sichere Transaktionsverarbeitung schützt sowohl Sie als auch Ihre Kunden vor Betrug. Sie erhalten Echtzeit-Zahlungsbenachrichtigungen, sodass Sie immer wissen, wann eine Zahlung eingegangen ist. Und das alles zu niedrigen Transaktionsgebühren, um Ihre Kosten zu minimieren.",
    icon: CreditCard,
    features: [
      "Mehrere Zahlungsmethoden",
      "Sichere Transaktionsverarbeitung",
      "Echtzeit-Zahlungsbenachrichtigungen",
      "Niedrige Transaktionsgebühren",
      "Einfache Integration",
      "Detaillierte Berichterstattung",
    ],
    benefits: [
      {
        title: "Vielfältige Zahlungsmethoden",
        description: "Unterstützung für alle gängigen Zahlungsarten",
        icon: CreditCard,
      },
      {
        title: "Höchste Sicherheit",
        description: "Modernste Verschlüsselung und Betrugsschutz",
        icon: ShieldCheck,
      },
      {
        title: "Transparente Gebühren",
        description: "Niedrige und transparente Transaktionskosten",
        icon: ThumbsUp,
      },
    ],
    testimonials: [
      {
        name: "Stefan Krause",
        position: "Inhaber, Online-Shop",
        content: "Die Zahlungslösung hat unsere Konversionsrate erhöht und die Abbruchquote im Checkout-Prozess deutlich reduziert.",
        rating: 5,
      },
      {
        name: "Nina Fischer",
        position: "E-Commerce Managerin",
        content: "Die Integration war einfach und die Vielfalt der Zahlungsmethoden ist beeindruckend. Unsere Kunden sind sehr zufrieden.",
        rating: 5,
      },
    ],
    stats: [
      { value: "10+", label: "Zahlungsmethoden" },
      { value: "99.99%", label: "Uptime" },
      { value: "< 1%", label: "Transaktionsgebühren" },
    ],
    imageSrc: "/placeholder.svg?height=400&width=600",
    imageAlt: "Online payment solutions",
    color: "bg-purple-50 dark:bg-purple-950/30",
    accentColor: "purple",
    gradientFrom: "from-purple-600",
    gradientTo: "to-pink-400",
  },
];

export default function ServicePage() {
  const params = useParams();
  const router = useRouter();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const serviceId = params.service as string;

  // Find the current service
  const service = services.find((s) => s.id === serviceId);

  // Scroll to top when page loads or service changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [serviceId]);

  // If service not found, redirect to home
  useEffect(() => {
    if (!service) {
      router.push("/");
    }
  }, [service, router]);

  if (!service) {
    return null;
  }

  // Other services for navigation
  const otherServices = services.filter((s) => s.id !== serviceId);

  // Handle phone number input
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPhoneNumber(value);
    // Simple validation - at least 10 digits
    setIsValid(value.replace(/\D/g, "").length >= 10);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid && consentChecked) {
      // In a real app, you would send this to your backend
      setSubmitted(true);
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setPhoneNumber("");
        setConsentChecked(false);
        setContactOpen(false);
      }, 3000);
    }
  };

  const { icon: Icon } = service;

  // Get accent color classes
  const getAccentClasses = (type: string) => {
    const colorMap: Record<string, Record<string, string>> = {
      red: {
        bg: "bg-red-600 dark:bg-red-700",
        hover: "hover:bg-red-700 dark:hover:bg-red-600",
        text: "text-red-600 dark:text-red-500",
        border: "border-red-600 dark:border-red-500",
        ring: "ring-red-600 dark:ring-red-500",
        gradient: "from-red-600 to-red-400 dark:from-red-700 dark:to-red-500",
      },
      orange: {
        bg: "bg-orange-600 dark:bg-orange-700",
        hover: "hover:bg-orange-700 dark:hover:bg-orange-600",
        text: "text-orange-600 dark:text-orange-500",
        border: "border-orange-600 dark:border-orange-500",
        ring: "ring-orange-600 dark:ring-orange-500",
        gradient: "from-orange-600 to-orange-400 dark:from-orange-700 dark:to-orange-500",
      },
      blue: {
        bg: "bg-blue-600 dark:bg-blue-700",
        hover: "hover:bg-blue-700 dark:hover:bg-blue-600",
        text: "text-blue-600 dark:text-blue-500",
        border: "border-blue-600 dark:border-blue-500",
        ring: "ring-blue-600 dark:ring-blue-500",
        gradient: "from-blue-600 to-blue-400 dark:from-blue-700 dark:to-blue-500",
      },
      green: {
        bg: "bg-green-600 dark:bg-green-700",
        hover: "hover:bg-green-700 dark:hover:bg-green-600",
        text: "text-green-600 dark:text-green-500",
        border: "border-green-600 dark:border-green-500",
        ring: "ring-green-600 dark:ring-green-500",
        gradient: "from-green-600 to-green-400 dark:from-green-700 dark:to-green-500",
      },
      purple: {
        bg: "bg-purple-600 dark:bg-purple-700",
        hover: "hover:bg-purple-700 dark:hover:bg-purple-600",
        text: "text-purple-600 dark:text-purple-500",
        border: "border-purple-600 dark:border-purple-500",
        ring: "ring-purple-600 dark:ring-purple-500",
        gradient: "from-purple-600 to-purple-400 dark:from-purple-700 dark:to-purple-500",
      },
    };

    return colorMap[service.accentColor]?.[type] || colorMap.red[type];
  };

  return (
    <div className="flex min-h-screen mx-auto flex-col">
      <main className="flex-1 relative">
        {/* Fixed Contact Form at Bottom */}
        <div className={cn("fixed bottom-0 left-0 right-0 z-40 transition-all duration-300 transform", contactOpen ? "translate-y-0" : "translate-y-[calc(100%-60px)]")}>
          <div className={cn("mx-auto max-w-lg shadow-lg rounded-t-lg overflow-hidden", getAccentClasses("bg"))}>
            <button onClick={() => setContactOpen(!contactOpen)} className="flex items-center justify-center p-4 text-white font-medium cursor-pointer w-full h-[60px]">
              {contactOpen ? (
                <>
                  <X className="h-6 w-6 mr-3" />
                  <span className="text-lg">Schließen</span>
                </>
              ) : (
                <>
                  <Phone className="h-6 w-6 mr-3" />
                  <span className="text-lg font-bold">Rückruf anfordern</span>
                </>
              )}
            </button>

            <div className={cn("bg-card text-card-foreground transition-all duration-300 overflow-hidden p-6", contactOpen ? "max-h-[400px]" : "max-h-0")}>
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="text-lg font-bold">Wir rufen Sie zurück</div>
                  <p className="text-muted-foreground">Hinterlassen Sie Ihre Telefonnummer und wir melden uns bei Ihnen.</p>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-base">
                      Telefonnummer
                    </Label>
                    <Input id="phone" placeholder="+49 123 4567890" value={phoneNumber} onChange={handlePhoneChange} className={cn("w-full text-base p-3 h-12", getAccentClasses("ring"))} />
                    <p className="text-xs text-muted-foreground">Beispiel: +49 123 4567890</p>
                  </div>

                  <div className="flex items-start space-x-2 pt-2">
                    <Checkbox id="consent" checked={consentChecked} onCheckedChange={(checked) => setConsentChecked(checked === true)} className="mt-1" />
                    <Label htmlFor="consent" className="text-sm leading-tight cursor-pointer">
                      Ich stimme zu, dass ich telefonisch kontaktiert werden darf.
                    </Label>
                  </div>

                  <Button type="submit" className={cn(getAccentClasses("bg"), getAccentClasses("hover"), "w-full py-6 text-base font-medium text-white")} disabled={!isValid || !consentChecked}>
                    <Phone className="h-5 w-5 mr-2" />
                    Rückruf anfordern
                  </Button>

                  <p className="text-xs text-muted-foreground pt-2">Wir respektieren Ihre Privatsphäre und werden Ihre Daten nicht an Dritte weitergeben.</p>
                </form>
              ) : (
                <div className="flex flex-col items-center justify-center p-6 bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300 rounded-md h-full">
                  <CheckCircle className="h-12 w-12 mb-4" />
                  <p className="text-center font-medium">Vielen Dank! Wir werden Sie in Kürze kontaktieren.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Service Hero */}
        <section className={cn("py-20 md:py-32 relative overflow-hidden", service.color)}>
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className={`absolute -top-20 -right-20 w-64 h-64 rounded-full bg-gradient-to-r ${service.gradientFrom} ${service.gradientTo} opacity-10 blur-3xl`}></div>
            <div className={`absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-gradient-to-r ${service.gradientFrom} ${service.gradientTo} opacity-10 blur-3xl`}></div>
          </div>

          <div className="container mx-auto px-4 md:px-6 relative z-10">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
              <div className="space-y-6">
                <div className={cn("inline-flex items-center gap-2 rounded-lg bg-card px-4 py-2 text-base")}>
                  <Icon className={cn("h-5 w-5", getAccentClasses("text"))} />
                  <span className="font-medium">{service.title}</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter">{service.title}</h1>
                <p className="text-xl md:text-2xl text-foreground/80 dark:text-foreground/70 leading-relaxed">{service.description}</p>
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button className={cn(getAccentClasses("bg"), getAccentClasses("hover"), "text-lg py-6 px-8 text-white")}>
                    Mehr erfahren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button variant="outline" className="text-lg py-6 px-8" onClick={() => setContactOpen(true)}>
                    Kontakt aufnehmen
                  </Button>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="relative group">
                  <div
                    className={cn("absolute inset-0 rounded-xl blur-xl opacity-30 group-hover:opacity-40 transition-opacity duration-500 bg-gradient-to-r", service.gradientFrom, service.gradientTo)}
                  ></div>
                  <Image
                    src={service.imageSrc || "/placeholder.svg"}
                    alt={service.imageAlt}
                    className="relative z-10 mx-auto aspect-video overflow-hidden rounded-xl object-cover object-center sm:w-full shadow-lg group-hover:shadow-xl transition-all duration-300"
                    width={600}
                    height={400}
                  />
                </div>
              </div>
            </div>

            {/* Stats section */}
            <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
              {service.stats.map((stat, index) => (
                <div key={index} className="bg-card dark:bg-card/80 rounded-xl p-8 text-center shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className={cn("text-4xl font-bold mb-2", getAccentClasses("text"))}>{stat.value}</div>
                  <div className="text-xl text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto space-y-8">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter text-center">Über unseren Service</h2>
              <p className="text-xl md:text-2xl text-foreground/80 dark:text-foreground/70 leading-relaxed">{service.longDescription}</p>
            </div>

            <div className="grid gap-12 md:grid-cols-3 mt-20">
              {service.benefits.map((benefit, index) => {
                const BenefitIcon = benefit.icon;
                return (
                  <div key={index} className="flex flex-col items-center text-center p-8 rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className={cn("p-4 rounded-full mb-6", service.color)}>
                      <BenefitIcon className={cn("h-10 w-10", getAccentClasses("text"))} />
                    </div>
                    <h3 className="text-2xl font-medium mb-4">{benefit.title}</h3>
                    <p className="text-lg text-muted-foreground">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className={cn("py-20 md:py-32", service.color)}>
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto space-y-8 text-center mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter">Unsere Leistungen</h2>
              <p className="text-xl md:text-2xl text-foreground/80 dark:text-foreground/70">Entdecken Sie die umfangreichen Funktionen und Vorteile unseres {service.title} Services.</p>
            </div>

            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {service.features.map((feature, index) => (
                <div key={index} className="flex items-start gap-4 p-6 rounded-xl bg-card shadow-sm hover:shadow-md transition-all duration-300 hover:translate-y-[-5px]">
                  <div className={cn("mt-1 p-2 rounded-full", service.color)}>
                    <CheckCircle className={cn("h-6 w-6", getAccentClasses("text"))} />
                  </div>
                  <div>
                    <h3 className="text-xl font-medium mb-2">{feature}</h3>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-16">
              <Button className={cn(getAccentClasses("bg"), getAccentClasses("hover"), "text-lg py-6 px-8 text-white")}>
                Alle Funktionen entdecken
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 md:py-32 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto space-y-8 text-center mb-16">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter">Kundenstimmen</h2>
              <p className="text-xl md:text-2xl text-foreground/80 dark:text-foreground/70">Erfahren Sie, was unsere Kunden über unseren {service.title} Service sagen.</p>
            </div>

            <div className="grid gap-8 md:grid-cols-2 max-w-5xl mx-auto">
              {service.testimonials.map((testimonial, index) => (
                <div key={index} className="p-8 rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-center gap-1 mb-6">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={cn("h-6 w-6", i < testimonial.rating ? getAccentClasses("text") : "text-gray-300 dark:text-gray-600")}
                        fill={i < testimonial.rating ? "currentColor" : "none"}
                      />
                    ))}
                  </div>
                  <blockquote className="text-xl text-foreground/90 mb-6 italic">"{testimonial.content}"</blockquote>
                  <div className="flex items-center gap-4">
                    <div className={cn("w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold text-white", getAccentClasses("bg"))}>{testimonial.name.charAt(0)}</div>
                    <div>
                      <div className="text-lg font-medium">{testimonial.name}</div>
                      <div className="text-muted-foreground">{testimonial.position}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Other Services Navigation */}
        <section className={cn("py-20 md:py-32", service.color)}>
          <div className="container mx-auto px-4 md:px-6">
            <div className="space-y-12">
              <div className="text-center space-y-6">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter">Entdecken Sie unsere anderen Dienste</h2>
                <p className="text-xl md:text-2xl text-foreground/80 dark:text-foreground/70 max-w-3xl mx-auto">
                  Wir bieten eine breite Palette von Dienstleistungen an, um Ihr Unternehmen zu unterstützen.
                </p>
              </div>

              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {otherServices.slice(0, 3).map((otherService) => {
                  const OtherIcon = otherService.icon;
                  const getOtherAccentClasses = (type: string) => {
                    const colorMap: Record<string, Record<string, string>> = {
                      red: {
                        bg: "bg-red-600 dark:bg-red-700",
                        text: "text-red-600 dark:text-red-500",
                        hover: "group-hover:text-red-700 dark:group-hover:text-red-400",
                      },
                      orange: {
                        bg: "bg-orange-600 dark:bg-orange-700",
                        text: "text-orange-600 dark:text-orange-500",
                        hover: "group-hover:text-orange-700 dark:group-hover:text-orange-400",
                      },
                      blue: {
                        bg: "bg-blue-600 dark:bg-blue-700",
                        text: "text-blue-600 dark:text-blue-500",
                        hover: "group-hover:text-blue-700 dark:group-hover:text-blue-400",
                      },
                      green: {
                        bg: "bg-green-600 dark:bg-green-700",
                        text: "text-green-600 dark:text-green-500",
                        hover: "group-hover:text-green-700 dark:group-hover:text-green-400",
                      },
                      purple: {
                        bg: "bg-purple-600 dark:bg-purple-700",
                        text: "text-purple-600 dark:text-purple-500",
                        hover: "group-hover:text-purple-700 dark:group-hover:text-purple-400",
                      },
                    };
                    return colorMap[otherService.accentColor]?.[type] || colorMap.red[type];
                  };

                  return (
                    <Link key={otherService.id} href={`/Services/${otherService.id}`}>
                      <div className="group relative overflow-hidden rounded-xl border border-border p-8 hover:border-foreground/20 transition-all duration-300 h-full flex flex-col bg-card shadow-sm hover:shadow-md">
                        <div className="absolute inset-0 bg-gradient-to-r from-background/50 to-background/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <div className="relative z-10 flex-1">
                          <div className="flex items-center gap-4 mb-6">
                            <div className={cn("p-3 rounded-full", otherService.color)}>
                              <OtherIcon className={cn("h-8 w-8", getOtherAccentClasses("text"))} />
                            </div>
                            <h3 className="text-2xl font-medium">{otherService.title}</h3>
                          </div>
                          <p className="text-lg text-muted-foreground mb-6">{otherService.description}</p>
                        </div>
                        <div className={cn("flex items-center text-lg font-medium transition-colors mt-4", getOtherAccentClasses("text"), getOtherAccentClasses("hover"), "relative z-10")}>
                          Mehr erfahren
                          <ChevronRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              <div className="flex justify-center pt-8">
                <Link href="/">
                  <Button variant="outline" className="gap-2 text-lg py-6 px-8">
                    <ArrowLeft className="h-5 w-5" />
                    Zurück zur Startseite
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
